import barcodes from '../server/utils/barcodes.js'
import {isServerAddedEmptyCollo} from './emptyCollo.js'
import {getOriginalDeliveryAddressIndex, getOriginalOrderAddresses, isRetourOrder} from './retourAddresses.js'
import {Order} from '../server/functions/orders/orders.types.js'

type CopyableCollo = {
    index?: number
    barcode?: string
    type?: string
    description?: string
    weight?: number
    length?: number
    width?: number
    height?: number
    pickupAddressIndex?: number
    deliveryAddressIndex?: number
    returned?: boolean
    originalDeliveryAddressIndex?: number
}

type CopiedOrder = Pick<Order, 'type' | 'retourId' | 'addresses' | 'originalAddresses' | 'history'>

/**
 * The pickup and delivery address of a collo in the copy of the order. The copy gets the addresses in the same order
 * (getOriginalOrderAddresses), so the indices stay valid. The returned colli of a retour belong to the retour address,
 * their delivery address comes from before the retour. Both are left out when one is invalid or can't be found, the
 * server then links the collo to the first pickup and last delivery address.
 */
function getCopiedAddressIndices(collo: CopyableCollo, order: CopiedOrder): {pickupAddressIndex?: number, deliveryAddressIndex?: number} {
    const addresses = getOriginalOrderAddresses(order)
    const returned = isRetourOrder(order) && !!collo.returned
    const {pickupAddressIndex} = collo
    const deliveryAddressIndex = returned ? getOriginalDeliveryAddressIndex(order, collo) : collo.deliveryAddressIndex

    if (returned && typeof deliveryAddressIndex !== 'number') {
        return {}
    }

    if (typeof pickupAddressIndex === 'number' && addresses[pickupAddressIndex]?.type !== 'pickup') {
        return {}
    }

    if (typeof deliveryAddressIndex === 'number' && addresses[deliveryAddressIndex]?.type !== 'delivery') {
        return {}
    }

    return {
        ...(typeof pickupAddressIndex === 'number' && {pickupAddressIndex}),
        ...(typeof deliveryAddressIndex === 'number' && {deliveryAddressIndex})
    }
}

// Copies the colli of an order for a new order: a collo with a barcode gets a new barcode, a collo without barcode stays without one
export function copyColli(colli: CopyableCollo[] = [], order?: CopiedOrder) {
    let trackTrace = ''

    const newColli = colli
        // An empty collo is added by the server to an address without colli, the new order gets its own
        .filter((collo) => !isServerAddedEmptyCollo(collo, colli))
        .map((collo, index) => {
            const hasBarcode = !!collo.barcode && collo.barcode !== 'no barcode'
            const barcode = hasBarcode ? barcodes.generate() : 'no barcode'

            // The barcode of the first collo with a barcode doubles as the track & trace code of the order
            if (hasBarcode && !trackTrace) {
                trackTrace = barcode
            }

            return {
                index,
                type: collo.type,
                description: collo.description,
                weight: collo.weight,
                length: collo.length,
                width: collo.width,
                height: collo.height,
                barcode,
                ...(order && getCopiedAddressIndices(collo, order))
            }
        })

    return {colli: newColli, trackTrace}
}
